import React from "react";
import signupImg from "../../assets/images/home/homeSlider.png";
import marketplace from "../../assets/images/how-it-works/marketplace.jpg";
import negotiation from "../../assets/images/how-it-works/negotiation.jpg";
import Connection from "../../assets/images/how-it-works/connection-management.jpg";
import thrive from "../../assets/images/how-it-works/thrivetogether.jpg";
import payments from "../../assets/images/how-it-works/payments.jpg";
import transparency from "../../assets/images/how-it-works/transparency.jpg";

const dividerStyle = {
  borderBottom: '1px solid #e0e0e0',
  margin: '0 auto',
  maxWidth: '1200px',
};

const StepIcon = ({ src, alt }) => (
  <img src={src} alt={alt} className="w-28 h-28 object-cover rounded-2xl bg-section-bg p-2 shadow" />
);

const benefitIcons = [
  // Emoji icons for each benefit card
  <span role="img" aria-label="Farmer" className="text-3xl">👨‍🌾</span>,
  <span role="img" aria-label="Company" className="text-3xl">🏢</span>,
  <span role="img" aria-label="Laborer" className="text-3xl">🛠️</span>,
  <span role="img" aria-label="Advantage" className="text-3xl">🌱</span>,
];

export const HowItWorks = () => {
  const steps = [
    {
      title: "Sign Up and Create Your Profile",
      description: "Farmers, food product companies, laborers, and equipment providers start their journey by signing up through our user-friendly app. Create your profile to unlock tailored opportunities.",
      image: signupImg,
    },
    {
      title: "Access a Vibrant Marketplace",
      description: "List your produce, explore fresh goods, or showcase your services. Our dynamic marketplace connects all stakeholders for seamless trade and collaboration.",
      image: marketplace,
    },
    {
      title: "Direct Bidding and Negotiations",
      description: "Bid and negotiate directly—no middlemen. Farmers and companies connect transparently to ensure fair deals and efficient procurement.",
      image: negotiation,
    },
    {
      title: "Seamless Connections and Engagement",
      description: "Engage with laborers and equipment providers, schedule tasks, and build lasting partnerships. Companies connect with reliable farmers for consistent supply.",
      image: Connection,
    },
    {
      title: "Transparency and Market Insights",
      description: "Access real-time pricing, market trends, and valuable insights. Make informed decisions and drive sustainable growth.",
      image: transparency,
    },
    {
      title: "Secure Transactions and Timely Payments",
      description: "Enjoy secure payments and prompt compensation for all parties. Trust and reliability are at the core of every transaction.",
      image: payments,
    },
    {
      title: "Thrive Together with Halyavara",
      description: "Join the agricultural revolution. Embrace sustainable practices, cultivate strong relationships, and empower every stakeholder for a brighter tomorrow.",
      image: thrive,
    },
  ];

  const benefitsData = [
    {
      title: "Farmers",
      points: [
        "Direct Transactions: Sell produce directly to companies for fair prices.",
        "Improved Market Access: Reach a larger network of buyers.",
        "Efficiency & Cost Savings: Streamlined processes save time and resources.",
        "Transparency: Real-time info for informed decisions.",
      ],
    },
    {
      title: "Food Product Companies",
      points: [
        "Access to Fresh Produce: Source high-quality goods directly.",
        "Cost Efficiency: Lower procurement costs, competitive pricing.",
        "Transparency & Traceability: Track origins and ensure quality.",
        "Diverse Supplier Network: Wide range of products and varieties.",
      ],
    },
    {
      title: "Laborers & Equipment Providers",
      points: [
        "Direct Connection: Partner directly with farmers.",
        "Increased Opportunities: Greater visibility and engagement.",
        "Efficient Scheduling: Optimize resource allocation.",
        "Prompt Payment: Timely, fair compensation.",
      ],
    },
    {
      title: "Overall Advantages",
      points: [
        "Sustainability: Support local farmers and eco-friendly practices.",
        "Empowerment: Foster prosperity for all stakeholders.",
        "Market Insights: Data-driven decisions for growth.",
        "User-Friendly: Seamless experience for everyone.",
      ],
    },
  ];

  return (
    <div style={{ background: '#f8f5f0', minHeight: '100vh', width: '100%' }}>
      {/* Hero Section */}
      <section className="py-20 px-4 text-center rounded-b-3xl shadow-lg relative overflow-hidden" style={{ background: 'transparent' }}>
        <h1 className="text-5xl md:text-6xl font-extrabold mb-4" style={{ color: '#205c20' }}>How Halyavara Works</h1>
        <p className="text-base md:text-lg max-w-2xl mx-auto font-medium mb-8" style={{ color: '#222' }}>
          Simplicity meets innovation—discover how we connect every link in the agricultural value chain for a fairer, smarter, and more sustainable future.
        </p>
      </section>
      <div style={dividerStyle}></div>

      {/* Step-by-Step Timeline Section */}
      <section className="py-20 px-4" style={{ background: 'transparent' }}>
        <div className="max-w-6xl mx-auto relative flex flex-col items-center">
          <div className="w-full flex flex-col items-center">
            {steps.map((step, idx) => (
              <React.Fragment key={step.title}>
                {/* Card Row */}
                <div className={`w-full flex ${idx % 2 === 0 ? 'justify-start' : 'justify-end'} mb-0`}>
                  <div className={`card-modern bg-white shadow-xl rounded-2xl p-10 flex flex-col md:flex-row items-center gap-10 w-full md:w-[60%] min-h-[280px] transition-transform duration-200 hover:shadow-2xl
                    ${idx % 2 === 0 ? 'md:ml-0 md:mr-auto' : 'md:mr-0 md:ml-auto'}`}
                    style={{ marginTop: 0, marginBottom: 0 }}>
                    <StepIcon src={step.image} alt={step.title} />
                    <div className="flex flex-col items-center md:items-start w-full">
                      <h2 className="text-3xl md:text-4xl font-bold mb-4 flex items-center gap-3" style={{ color: '#205c20' }}>{step.title}</h2>
                      <p className="text-base md:text-lg" style={{ color: '#222' }}>{step.description}</p>
                    </div>
                  </div>
                </div>
                {/* Timeline Dot and Line (except after last card) */}
                {idx < steps.length - 1 && (
                  <div className="flex flex-col items-center" style={{ minHeight: '80px' }}>
                    <div className="w-1 h-6 bg-green-200"></div>
                    <div className="w-7 h-7 rounded-full bg-green-500 border-4 border-white shadow-lg animate-pulse z-10"></div>
                    <div className="w-1 h-6 bg-green-200"></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
      <div style={dividerStyle}></div>

      {/* Benefits Section */}
      <section className="py-20 px-4" style={{ background: 'transparent' }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          {benefitsData.map((benefit, idx) => (
            <div key={benefit.title} className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-bold mb-4 mt-2 flex items-center gap-3" style={{ color: '#205c20' }}>
                {benefitIcons[idx]}
                <span>{benefit.title}</span>
              </h2>
              <ul className="list-disc pl-6 text-base" style={{ color: '#222' }}>
                {benefit.points.map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
      <div style={dividerStyle}></div>

      {/* CTA Section */}
      <section className="py-16 px-4 text-center" style={{ background: 'transparent' }}>
        <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ color: '#205c20' }}>Ready to Experience the Future of Farming?</h2>
        <p className="text-base mb-8 max-w-2xl mx-auto" style={{ color: '#222' }}>
          Join Halyavara today and be part of a movement that’s transforming Indian agriculture for good.
        </p>
        <a href="/contact-us" className="btn-modern">Get Started</a>
      </section>
    </div>
  );
};
